import React, { createContext, useContext, useState, useRef, useCallback } from 'react';

const ActivityLogContext = createContext();

export const useActivityLog = () => {
  const context = useContext(ActivityLogContext);
  if (!context) {
    throw new Error('useActivityLog must be used within an ActivityLogProvider');
  }
  return context;
};

export const ActivityLogProvider = ({ children }) => {
  const [processes, setProcesses] = useState({});
  const [panelOpen, setPanelOpen] = useState(false);
  const [panelMinimized, setPanelMinimized] = useState(false);
  const [activeProcessId, setActiveProcessId] = useState(null);
  const intervalsRef = useRef({});

  // Add a new process to track
  const addActivityProcess = useCallback((processId, name, interfaceName) => {
    setProcesses(prev => ({
      ...prev,
      [processId]: {
        id: processId,
        name,
        interfaceName,
        status: 'processing',
        logs: [],
        progressPercentage: 0,
        totalItems: 0,
        processedItems: 0,
        startTime: new Date(),
        endTime: null,
      }
    }));
    setActiveProcessId(processId);
    setPanelOpen(true);
    setPanelMinimized(false);
    return processId;
  }, []);

  // Update process status
  const updateActivityProcess = useCallback((processId, updates) => {
    setProcesses(prev => {
      if (!prev[processId]) return prev;
      return {
        ...prev,
        [processId]: {
          ...prev[processId],
          ...updates,
        }
      };
    });
  }, []);

  // Add log to a process
  const addLog = useCallback((processId, log) => {
    setProcesses(prev => {
      if (!prev[processId]) return prev;
      return {
        ...prev,
        [processId]: {
          ...prev[processId],
          logs: [...prev[processId].logs, log],
        }
      };
    });
  }, []);

  // Set logs for a process (replace all)
  const setLogs = useCallback((processId, logs) => {
    setProcesses(prev => {
      if (!prev[processId]) return prev;
      return {
        ...prev,
        [processId]: {
          ...prev[processId],
          logs,
        }
      };
    });
  }, []);

  // Mark process as completed
  const completeActivityProcess = useCallback((processId, success = true) => {
    setProcesses(prev => {
      if (!prev[processId]) return prev;
      return {
        ...prev,
        [processId]: {
          ...prev[processId],
          status: success ? 'completed' : 'error',
          endTime: new Date(),
          progressPercentage: success ? 100 : prev[processId].progressPercentage,
        }
      };
    });
  }, []);

  // Remove a process
  const removeActivityProcess = useCallback((processId) => {
    setProcesses(prev => {
      const newProcesses = { ...prev };
      delete newProcesses[processId];

      // If we removed the active process, switch to another one
      if (activeProcessId === processId) {
        const remainingIds = Object.keys(newProcesses);
        setActiveProcessId(remainingIds.length > 0 ? remainingIds[remainingIds.length - 1] : null);
      }

      // Close panel if no processes left
      if (Object.keys(newProcesses).length === 0) {
        setPanelOpen(false);
      }

      return newProcesses;
    });

    // Clear interval if exists
    if (intervalsRef.current[processId]) {
      clearInterval(intervalsRef.current[processId]);
      delete intervalsRef.current[processId];
    }
  }, [activeProcessId]);

  // Clear all completed/error processes
  const clearFinishedProcesses = useCallback(() => {
    setProcesses(prev => {
      const newProcesses = {};
      Object.entries(prev).forEach(([id, process]) => {
        if (process.status === 'processing') {
          newProcesses[id] = process;
        }
      });

      if (Object.keys(newProcesses).length === 0) {
        setPanelOpen(false);
        setActiveProcessId(null);
      } else if (!newProcesses[activeProcessId]) {
        setActiveProcessId(Object.keys(newProcesses)[0]);
      }

      return newProcesses;
    });
  }, [activeProcessId]);

  // Store interval reference
  const setIntervalRef = useCallback((processId, intervalId) => {
    intervalsRef.current[processId] = intervalId;
  }, []);

  // Clear interval reference
  const clearIntervalRef = useCallback((processId) => {
    if (intervalsRef.current[processId]) {
      clearInterval(intervalsRef.current[processId]);
      delete intervalsRef.current[processId];
    }
  }, []);

  const value = {
    processes,
    panelOpen,
    setPanelOpen,
    panelMinimized,
    setPanelMinimized,
    activeProcessId,
    setActiveProcessId,
    addActivityProcess,
    updateActivityProcess,
    addLog,
    setLogs,
    completeActivityProcess,
    removeActivityProcess,
    clearFinishedProcesses,
    setIntervalRef,
    clearIntervalRef,
  };

  return (
    <ActivityLogContext.Provider value={value}>
      {children}
    </ActivityLogContext.Provider>
  );
};

export default ActivityLogContext;
